import cloneDeep from 'lodash/cloneDeep';
import { handleActions } from 'redux-actions';
import { getEntryRowAndDateString } from '../../containers/Timesheet/helpers';
import { SimpleAction } from '../../types/redux/actions';
import { TimesheetEntriesStore, TimesheetEntriesStoreItem } from '../../types/redux/stores/timesheetEntries.types';
import { logout } from '../authentication-slice';
import * as types from './actionDefinitions';

const initialState: TimesheetEntriesStore = {
  data: {},
  disabledEntries: {},
  error: null,
  initialLoad: false,
  loading: false,
  saving: false,
};

const createTimesheetEntryRequest = (state: TimesheetEntriesStore, payload: any): TimesheetEntriesStore => {
  const { timesheetRowId, date } = payload.data.data;
  const disabledEntries = {
    ...state.disabledEntries,
  };
  if (timesheetRowId && date) {
    const entryCustomId = getEntryRowAndDateString({ timesheetRowId, date });
    if (entryCustomId) {
      disabledEntries[entryCustomId] = true;
    }
  }
  return {
    ...state,
    disabledEntries,
    error: null,
    saving: true,
  };
};
const createTimesheetEntryRequestFailure = (state: TimesheetEntriesStore, action: any): TimesheetEntriesStore => {
  return {
    ...state,
    error: action.error,
    saving: false,
  };
};
const createTimesheetEntryRequestSuccess = (state: TimesheetEntriesStore, payload: any): TimesheetEntriesStore => {
  const data = cloneDeep(state.data);
  data[payload.data.id] = { ...payload.data };
  return {
    ...state,
    data,
    error: null,
    saving: false,
  };
};
const createTimesheetEntryRequestFinally = (state: TimesheetEntriesStore, payload: any): TimesheetEntriesStore => {
  const disabledEntries = {
    ...state.disabledEntries,
  };
  const { timesheetRowId, date } = payload.data;
  if (timesheetRowId && date) {
    const entryId = getEntryRowAndDateString({ timesheetRowId, date });
    delete disabledEntries[entryId];
  }
  return {
    ...state,
    disabledEntries,
    error: null,
    saving: false,
  };
};

const deleteTimesheetEntryRequest = (state: TimesheetEntriesStore): TimesheetEntriesStore => {
  return {
    ...state,
    error: null,
    saving: true,
  };
};
const deleteTimesheetEntryRequestFailure = (state: TimesheetEntriesStore, action: any): TimesheetEntriesStore => {
  return {
    ...state,
    error: action.error,
    saving: false,
  };
};
const deleteTimesheetEntryRequestSuccess = (
  state: TimesheetEntriesStore,
  action: { payload: { entryId: string } },
): TimesheetEntriesStore => {
  if (!action.payload.entryId) return state;
  const data = cloneDeep(state.data);
  delete data[action.payload.entryId];
  return {
    ...state,
    data,
    error: null,
    saving: false,
  };
};

const getTimesheetEntriesRequest = (state: TimesheetEntriesStore): TimesheetEntriesStore => {
  return {
    ...state,
    error: null,
    loading: true,
    saving: false,
  };
};
const getTimesheetEntriesRequestFailure = (state: TimesheetEntriesStore, action: any): TimesheetEntriesStore => {
  return {
    ...state,
    error: action.error,
    loading: false,
    saving: false,
  };
};
const getTimesheetEntriesRequestSuccess = (state: TimesheetEntriesStore, action: SimpleAction): TimesheetEntriesStore => {
  const arrayToObject = action.payload.reduce((acc: any, curr: TimesheetEntriesStoreItem) => {
    acc[curr.id] = curr;
    return acc;
  }, {});
  return {
    ...state,
    data: arrayToObject,
    error: null,
    initialLoad: true,
    loading: false,
    saving: false,
  };
};

const resetTimesheetEntriesState = (): TimesheetEntriesStore => {
  return initialState;
};

const updateTimesheetEntryRequest = (state: TimesheetEntriesStore, payload: any): TimesheetEntriesStore => {
  const disabledEntries = {
    ...state.disabledEntries,
  };
  const id = payload.data?.entryId;
  if (id) {
    disabledEntries[id] = true;
  }
  return {
    ...state,
    disabledEntries,
    error: null,
    saving: true,
  };
};
const updateTimesheetEntryRequestFailure = (state: TimesheetEntriesStore, action: any): TimesheetEntriesStore => {
  return {
    ...state,
    error: action.error,
    saving: false,
  };
};
const updateTimesheetEntryRequestSuccess = (state: TimesheetEntriesStore, payload: any): TimesheetEntriesStore => {
  if (!payload.data?.id) return state;
  const data = cloneDeep(state.data);
  data[payload.data.id] = { ...data[payload.data.id], ...payload.data };
  return {
    ...state,
    data,
    error: null,
    saving: false,
  };
};
const updateTimesheetEntryRequestFinally = (state: TimesheetEntriesStore, payload: any): TimesheetEntriesStore => {
  const id = payload.data;
  if (!id) return state;
  const disabledEntries = {
    ...state.disabledEntries,
  };
  delete disabledEntries[id];
  return {
    ...state,
    disabledEntries,
    error: null,
    saving: false,
  };
};

const logoutRequest = () => initialState;

export default handleActions(
  {
    [types.CREATE_TIMESHEET_ENTRY_REQUEST]: createTimesheetEntryRequest,
    [types.CREATE_TIMESHEET_ENTRY_REQUEST_FAILURE]: createTimesheetEntryRequestFailure,
    [types.CREATE_TIMESHEET_ENTRY_REQUEST_SUCCESS]: createTimesheetEntryRequestSuccess,
    [types.CREATE_TIMESHEET_ENTRY_REQUEST_FINALLY]: createTimesheetEntryRequestFinally,
    [types.CREATE_ENTRY_AND_ASSIGN_CLUSTER_REQUEST]: createTimesheetEntryRequest, // Same reducer as simple create
    [types.CREATE_ENTRY_AND_ASSIGN_CLUSTER_REQUEST_FAILURE]: createTimesheetEntryRequestFailure, // Same reducer as simple create
    [types.CREATE_ENTRY_AND_ASSIGN_CLUSTER_REQUEST_SUCCESS]: createTimesheetEntryRequestSuccess, // Same reducer as simple create
    [types.CREATE_ENTRY_AND_ASSIGN_NODE_REQUEST]: createTimesheetEntryRequest, // Same reducer as simple create
    [types.CREATE_ENTRY_AND_ASSIGN_NODE_REQUEST_FAILURE]: createTimesheetEntryRequestFailure, // Same reducer as simple create
    [types.CREATE_ENTRY_AND_ASSIGN_NODE_REQUEST_SUCCESS]: createTimesheetEntryRequestSuccess, // Same reducer as simple create
    [types.DELETE_TIMESHEET_ENTRY_REQUEST]: deleteTimesheetEntryRequest,
    [types.DELETE_TIMESHEET_ENTRY_REQUEST_FAILURE]: deleteTimesheetEntryRequestFailure,
    [types.DELETE_TIMESHEET_ENTRY_REQUEST_SUCCESS]: deleteTimesheetEntryRequestSuccess,
    [types.GET_TIMESHEET_ENTRIES_REQUEST]: getTimesheetEntriesRequest,
    [types.GET_TIMESHEET_ENTRIES_REQUEST_FAILURE]: getTimesheetEntriesRequestFailure,
    [types.GET_TIMESHEET_ENTRIES_REQUEST_SUCCESS]: getTimesheetEntriesRequestSuccess,
    [types.RESET_TIMESHEET_ENTRIES_STATE]: resetTimesheetEntriesState,
    [types.UPDATE_TIMESHEET_ENTRY_REQUEST]: updateTimesheetEntryRequest,
    [types.UPDATE_TIMESHEET_ENTRY_REQUEST_FAILURE]: updateTimesheetEntryRequestFailure,
    [types.UPDATE_TIMESHEET_ENTRY_REQUEST_SUCCESS]: updateTimesheetEntryRequestSuccess,
    [types.UPDATE_TIMESHEET_ENTRY_REQUEST_FINALLY]: updateTimesheetEntryRequestFinally,
    [logout.type]: logoutRequest,
  },
  initialState,
);
